import { useEffect, useRef, useState } from 'react';
import { AlertSystem } from '../services/AlertSystem';
import { AlertUI } from '../components/AlertUI';
import { UserInfo } from '../types';

export const useAlertUI = (userInfo: UserInfo | null = null) => {
  const alertUIRef = useRef<AlertUI | null>(null);
  const [alertCount, setAlertCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const updateAlertCount = () => {
    if (alertUIRef.current) {
      const count = alertUIRef.current.getAlertCount();
      setAlertCount(count);
    }
  };

  useEffect(() => {
    let mounted = true;

    const initAlerts = async () => {
      try {
        setIsLoading(true);
        const alertSystem = await AlertSystem.initialize(userInfo);

        if (!mounted) return;

        alertUIRef.current = new AlertUI(alertSystem);
        alertUIRef.current.init();

        updateAlertCount();
      } catch (error) {
        console.error('Error initializing alerts:', error);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    initAlerts();

    return () => {
      mounted = false;
      if (alertUIRef.current) {
        alertUIRef.current.destroy();
        alertUIRef.current = null;
      }
    };
  }, [userInfo]);

  useEffect(() => {
    const handleAlertDismissed = (event: CustomEvent) => {
      setAlertCount(event.detail.remainingCount);
    };

    window.addEventListener('alertDismissed', handleAlertDismissed as EventListener);

    return () => {
      window.removeEventListener('alertDismissed', handleAlertDismissed as EventListener);
    };
  }, []);

  const showMobileAlerts = () => {
    if (alertUIRef.current) {
      alertUIRef.current.showMobileAlerts();
      setTimeout(updateAlertCount, 100);
    }
  };

  const hideMobileAlerts = () => {
    if (alertUIRef.current) {
      alertUIRef.current.hideMobileAlerts();
    }
  };

  const refreshAlerts = async () => {
    if (alertUIRef.current) {
      try {
        setIsLoading(true);
        const alertSystem = await AlertSystem.initialize(userInfo);

        alertUIRef.current.destroy();
        alertUIRef.current = new AlertUI(alertSystem);
        alertUIRef.current.init();

        const alertsToShow = alertSystem.getAlertsToShow();
        setAlertCount(alertsToShow.length);
      } catch (error) {
        console.error('Error refreshing alerts:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return {
    alertCount,
    isLoading,
    showMobileAlerts,
    hideMobileAlerts,
    refreshAlerts,
  };
};
